// Mock data for the Parcelio logistics platform.
// Pure data — no side effects. Used across dashboard, tracking, drivers, wallet, etc.

export type ParcelStatus = "pending" | "in_transit" | "out_for_delivery" | "delivered" | "exception";

export interface Parcel {
  id: string;
  tracking: string;
  recipient: string;
  address: string;
  city: string;
  status: ParcelStatus;
  driverId: string;
  eta: string; // human string
  progress: number; // 0..100
  weightKg: number;
  value: number; // NGN
  updatedAt: string;
  origin: { lat: number; lng: number; label: string };
  destination: { lat: number; lng: number; label: string };
  current: { lat: number; lng: number };
  timeline: { at: string; label: string; note?: string; done: boolean }[];
}

export interface Driver {
  id: string;
  name: string;
  phone: string;
  avatarInitials: string;
  vehicle: string;
  plate: string;
  rating: number;
  status: "online" | "on_delivery" | "offline" | "break";
  activeParcels: number;
  completedToday: number;
  location: string;
}

export type VehicleType = "bike" | "van" | "truck" | "car";
export type VehicleStatus = "active" | "maintenance" | "idle" | "retired";

export interface Vehicle {
  id: string;
  plate: string;
  type: VehicleType;
  model: string;
  year: number;
  capacityKg: number;
  status: VehicleStatus;
  assignedDriverId?: string;
  odometerKm: number;
  lastServiceAt: string;
  nextServiceKm: number;
  fuel: "petrol" | "diesel" | "electric";
}

export interface Conversation {
  id: string;
  parcelId: string;
  with: string;
  role: "driver" | "customer" | "support";
  lastMessage: string;
  lastAt: string;
  unread: number;
  avatarInitials: string;
}

export interface Message {
  id: string;
  from: "me" | "them";
  text: string;
  at: string;
}

export interface Transaction {
  id: string;
  reference: string;
  type: "topup" | "payout" | "delivery_fee" | "refund";
  provider: "paystack" | "stripe" | "wallet";
  amount: number; // NGN, negative for outflows
  status: "success" | "pending" | "failed";
  at: string;
  parcelId?: string;
  method: string;
}

export interface AlertItem {
  id: string;
  severity: "critical" | "warning" | "info";
  title: string;
  detail: string;
  at: string;
}

export const drivers: Driver[] = [
  { id: "D-101", name: "Amaka Obi", phone: "+234 803 555 0142", avatarInitials: "AO", vehicle: "Bike · Honda 125", plate: "LAG-284-KJA", rating: 4.9, status: "on_delivery", activeParcels: 3, completedToday: 12, location: "Yaba, Lagos" },
  { id: "D-102", name: "Tunde Bakare", phone: "+234 802 555 0198", avatarInitials: "TB", vehicle: "Van · Toyota HiAce", plate: "LAG-771-EPE", rating: 4.7, status: "online", activeParcels: 0, completedToday: 8, location: "Ikeja, Lagos" },
  { id: "D-103", name: "Chioma Nwosu", phone: "+234 809 555 0110", avatarInitials: "CN", vehicle: "Bike · Yamaha 150", plate: "LAG-902-IKJ", rating: 4.95, status: "on_delivery", activeParcels: 2, completedToday: 15, location: "Lekki, Lagos" },
  { id: "D-104", name: "Femi Adeyemi", phone: "+234 806 555 0134", avatarInitials: "FA", vehicle: "Bike · Bajaj", plate: "LAG-118-VIC", rating: 4.6, status: "break", activeParcels: 1, completedToday: 6, location: "Surulere, Lagos" },
  { id: "D-105", name: "Grace Okoro", phone: "+234 810 555 0187", avatarInitials: "GO", vehicle: "Van · Ford Transit", plate: "LAG-556-AJA", rating: 4.8, status: "offline", activeParcels: 0, completedToday: 0, location: "Ajah, Lagos" },
  { id: "D-106", name: "Kunle Ojo", phone: "+234 813 555 0165", avatarInitials: "KO", vehicle: "Bike · Suzuki", plate: "LAG-334-MRD", rating: 4.85, status: "on_delivery", activeParcels: 2, completedToday: 10, location: "Maryland, Lagos" },
];

export const vehicles: Vehicle[] = [
  { id: "V-201", plate: "LAG-284-KJA", type: "bike", model: "Honda 125", year: 2023, capacityKg: 25, status: "active", assignedDriverId: "D-101", odometerKm: 18420, lastServiceAt: "May 12, 2026", nextServiceKm: 20000, fuel: "petrol" },
  { id: "V-202", plate: "LAG-771-EPE", type: "van", model: "Toyota HiAce", year: 2021, capacityKg: 900, status: "active", assignedDriverId: "D-102", odometerKm: 88240, lastServiceAt: "Jun 02, 2026", nextServiceKm: 92000, fuel: "diesel" },
  { id: "V-203", plate: "LAG-902-IKJ", type: "bike", model: "Yamaha 150", year: 2024, capacityKg: 30, status: "active", assignedDriverId: "D-103", odometerKm: 9420, lastServiceAt: "Jun 18, 2026", nextServiceKm: 12000, fuel: "petrol" },
  { id: "V-204", plate: "LAG-118-VIC", type: "bike", model: "Bajaj Boxer", year: 2022, capacityKg: 25, status: "maintenance", assignedDriverId: "D-104", odometerKm: 42110, lastServiceAt: "Apr 22, 2026", nextServiceKm: 45000, fuel: "petrol" },
  { id: "V-205", plate: "LAG-556-AJA", type: "van", model: "Ford Transit", year: 2020, capacityKg: 1200, status: "idle", assignedDriverId: "D-105", odometerKm: 132400, lastServiceAt: "Mar 30, 2026", nextServiceKm: 140000, fuel: "diesel" },
  { id: "V-206", plate: "LAG-334-MRD", type: "bike", model: "Suzuki GN", year: 2023, capacityKg: 25, status: "active", assignedDriverId: "D-106", odometerKm: 21050, lastServiceAt: "May 28, 2026", nextServiceKm: 24000, fuel: "petrol" },
  { id: "V-207", plate: "LAG-712-OSH", type: "truck", model: "Isuzu NPR", year: 2019, capacityKg: 3500, status: "active", odometerKm: 214800, lastServiceAt: "Jun 10, 2026", nextServiceKm: 220000, fuel: "diesel" },
  { id: "V-208", plate: "LAG-449-EV", type: "car", model: "BYD Atto 3", year: 2025, capacityKg: 400, status: "idle", odometerKm: 3120, lastServiceAt: "Jun 24, 2026", nextServiceKm: 15000, fuel: "electric" },
];

export const parcels: Parcel[] = [
  {
    id: "P-98421",
    tracking: "PCL98421NG",
    recipient: "Ifeoma Eze",
    address: "12B Admiralty Way, Lekki Phase 1",
    city: "Lagos",
    status: "out_for_delivery",
    driverId: "D-101",
    eta: "14 min",
    progress: 78,
    weightKg: 2.4,
    value: 45000,
    updatedAt: "2 min ago",
    origin: { lat: 6.5244, lng: 3.3792, label: "Parcelio Hub · Yaba" },
    destination: { lat: 6.4478, lng: 3.4723, label: "Lekki Phase 1" },
    current: { lat: 6.485, lng: 3.43 },
    timeline: [
      { at: "08:12", label: "Order received", done: true },
      { at: "09:04", label: "Picked up from sender", done: true },
      { at: "10:20", label: "Arrived at hub", note: "Sorted for Lekki route", done: true },
      { at: "11:45", label: "Out for delivery", note: "Assigned to Amaka O.", done: true },
      { at: "—", label: "Delivered", done: false },
    ],
  },
  {
    id: "P-98418",
    tracking: "PCL98418NG",
    recipient: "Michael Adebayo",
    address: "44 Allen Avenue, Ikeja",
    city: "Lagos",
    status: "in_transit",
    driverId: "D-102",
    eta: "38 min",
    progress: 42,
    weightKg: 0.8,
    value: 12500,
    updatedAt: "5 min ago",
    origin: { lat: 6.5244, lng: 3.3792, label: "Parcelio Hub · Yaba" },
    destination: { lat: 6.6018, lng: 3.3515, label: "Allen, Ikeja" },
    current: { lat: 6.56, lng: 3.37 },
    timeline: [
      { at: "09:30", label: "Order received", done: true },
      { at: "10:10", label: "Picked up", done: true },
      { at: "11:02", label: "In transit", done: true },
      { at: "—", label: "Out for delivery", done: false },
      { at: "—", label: "Delivered", done: false },
    ],
  },
  {
    id: "P-98410",
    tracking: "PCL98410NG",
    recipient: "Sarah Johnson",
    address: "7 Bourdillon Rd, Ikoyi",
    city: "Lagos",
    status: "delivered",
    driverId: "D-103",
    eta: "Delivered",
    progress: 100,
    weightKg: 1.2,
    value: 28000,
    updatedAt: "1 hr ago",
    origin: { lat: 6.5244, lng: 3.3792, label: "Parcelio Hub · Yaba" },
    destination: { lat: 6.4505, lng: 3.4372, label: "Ikoyi" },
    current: { lat: 6.4505, lng: 3.4372 },
    timeline: [
      { at: "07:45", label: "Order received", done: true },
      { at: "08:30", label: "Picked up", done: true },
      { at: "09:50", label: "Out for delivery", done: true },
      { at: "10:42", label: "Delivered", note: "Signed by S. Johnson", done: true },
    ],
  },
  {
    id: "P-98407",
    tracking: "PCL98407NG",
    recipient: "David Okafor",
    address: "18 Aromire Ave, Ikeja GRA",
    city: "Lagos",
    status: "exception",
    driverId: "D-104",
    eta: "Action needed",
    progress: 60,
    weightKg: 3.1,
    value: 67000,
    updatedAt: "12 min ago",
    origin: { lat: 6.5244, lng: 3.3792, label: "Parcelio Hub · Yaba" },
    destination: { lat: 6.5833, lng: 3.3667, label: "Ikeja GRA" },
    current: { lat: 6.55, lng: 3.37 },
    timeline: [
      { at: "08:00", label: "Order received", done: true },
      { at: "09:12", label: "Picked up", done: true },
      { at: "10:33", label: "Out for delivery", done: true },
      { at: "11:20", label: "Delivery failed", note: "Recipient unavailable — retry scheduled", done: true },
    ],
  },
  {
    id: "P-98402",
    tracking: "PCL98402NG",
    recipient: "Blessing Umeh",
    address: "3 Adeola Odeku, VI",
    city: "Lagos",
    status: "pending",
    driverId: "D-106",
    eta: "Awaiting pickup",
    progress: 8,
    weightKg: 0.5,
    value: 9800,
    updatedAt: "just now",
    origin: { lat: 6.5244, lng: 3.3792, label: "Sender · Yaba" },
    destination: { lat: 6.4281, lng: 3.4219, label: "Victoria Island" },
    current: { lat: 6.5244, lng: 3.3792 },
    timeline: [
      { at: "12:04", label: "Order received", done: true },
      { at: "—", label: "Picked up", done: false },
    ],
  },
  {
    id: "P-98399",
    tracking: "PCL98399NG",
    recipient: "James Aluko",
    address: "22 Opebi Rd, Ikeja",
    city: "Lagos",
    status: "in_transit",
    driverId: "D-106",
    eta: "52 min",
    progress: 30,
    weightKg: 4.5,
    value: 88000,
    updatedAt: "8 min ago",
    origin: { lat: 6.5244, lng: 3.3792, label: "Parcelio Hub · Yaba" },
    destination: { lat: 6.5941, lng: 3.3617, label: "Opebi" },
    current: { lat: 6.55, lng: 3.37 },
    timeline: [
      { at: "10:15", label: "Order received", done: true },
      { at: "11:00", label: "Picked up", done: true },
      { at: "11:30", label: "In transit", done: true },
      { at: "—", label: "Out for delivery", done: false },
      { at: "—", label: "Delivered", done: false },
    ],
  },
];

export const conversations: Conversation[] = [
  { id: "C-1", parcelId: "P-98421", with: "Amaka Obi", role: "driver", lastMessage: "Arriving in ~10 min, calling the recipient now.", lastAt: "2m", unread: 2, avatarInitials: "AO" },
  { id: "C-2", parcelId: "P-98407", with: "David Okafor", role: "customer", lastMessage: "I'll be back home after 4pm — please retry.", lastAt: "18m", unread: 1, avatarInitials: "DO" },
  { id: "C-3", parcelId: "P-98418", with: "Tunde Bakare", role: "driver", lastMessage: "Traffic is heavy on Third Mainland.", lastAt: "24m", unread: 0, avatarInitials: "TB" },
  { id: "C-4", parcelId: "P-98410", with: "Sarah Johnson", role: "customer", lastMessage: "Received — thanks!", lastAt: "1h", unread: 0, avatarInitials: "SJ" },
  { id: "C-5", parcelId: "P-98402", with: "Support Bot", role: "support", lastMessage: "Pickup slot confirmed for 12:45.", lastAt: "2h", unread: 0, avatarInitials: "SP" },
];

export const messagesByConversation: Record<string, Message[]> = {
  "C-1": [
    { id: "m1", from: "them", text: "Hi, I've picked up parcel PCL98421NG.", at: "11:46" },
    { id: "m2", from: "me", text: "Great — recipient is Ifeoma Eze, contact +234 807 …", at: "11:47" },
    { id: "m3", from: "them", text: "On my way to Lekki now.", at: "11:48" },
    { id: "m4", from: "them", text: "Arriving in ~10 min, calling the recipient now.", at: "12:14" },
  ],
  "C-2": [
    { id: "m1", from: "them", text: "I'll be back home after 4pm — please retry.", at: "11:52" },
  ],
  "C-3": [
    { id: "m1", from: "them", text: "Traffic is heavy on Third Mainland.", at: "11:46" },
  ],
  "C-4": [
    { id: "m1", from: "them", text: "Received — thanks!", at: "10:44" },
  ],
  "C-5": [
    { id: "m1", from: "them", text: "Pickup slot confirmed for 12:45.", at: "10:04" },
  ],
};

export const transactions: Transaction[] = [
  { id: "T-2041", reference: "PSK_9F2A1B", type: "topup", provider: "paystack", amount: 250000, status: "success", at: "Today, 09:12", method: "Card •• 4821" },
  { id: "T-2040", reference: "STR_7C4D9E", type: "topup", provider: "stripe", amount: 500000, status: "success", at: "Yesterday, 18:44", method: "Card •• 1029" },
  { id: "T-2039", reference: "DLV-98421", type: "delivery_fee", provider: "wallet", amount: -3500, status: "success", at: "Today, 11:45", parcelId: "P-98421", method: "Wallet" },
  { id: "T-2038", reference: "DLV-98418", type: "delivery_fee", provider: "wallet", amount: -2200, status: "success", at: "Today, 11:02", parcelId: "P-98418", method: "Wallet" },
  { id: "T-2037", reference: "PAY-D101", type: "payout", provider: "paystack", amount: -84500, status: "pending", at: "Today, 08:00", method: "Bank transfer" },
  { id: "T-2036", reference: "DLV-98407", type: "refund", provider: "wallet", amount: 4200, status: "success", at: "Today, 11:22", parcelId: "P-98407", method: "Wallet" },
  { id: "T-2035", reference: "STR_5B2A", type: "topup", provider: "stripe", amount: 120000, status: "failed", at: "Yesterday, 22:18", method: "Card •• 7712" },
  { id: "T-2034", reference: "DLV-98410", type: "delivery_fee", provider: "wallet", amount: -1800, status: "success", at: "Today, 09:50", parcelId: "P-98410", method: "Wallet" },
];

export const alerts: AlertItem[] = [
  { id: "A-1", severity: "critical", title: "Delivery failed — PCL98407NG", detail: "Recipient unavailable at Ikeja GRA. Retry scheduled.", at: "12m" },
  { id: "A-2", severity: "warning", title: "Driver break exceeded — Femi A.", detail: "On break for 42 min (limit 30).", at: "20m" },
  { id: "A-3", severity: "info", title: "Payout pending", detail: "₦84,500 payout to Amaka O. awaiting bank confirmation.", at: "1h" },
];

export const walletBalance = {
  available: 1_284_500,
  pending: 84_500,
  currency: "NGN",
};

export const kpis = {
  activeDeliveries: 24,
  completedToday: 51,
  onTimeRate: 96.4,
  activeDrivers: 12,
  revenueToday: 428_200,
  exceptions: 2,
};

export function formatNaira(amount: number) {
  const sign = amount < 0 ? "-" : "";
  return `${sign}₦${Math.abs(amount).toLocaleString("en-NG")}`;
}

export function statusLabel(s: ParcelStatus) {
  return {
    pending: "Pending",
    in_transit: "In transit",
    out_for_delivery: "Out for delivery",
    delivered: "Delivered",
    exception: "Exception",
  }[s];
}

export function statusTone(s: ParcelStatus): "info" | "warning" | "success" | "destructive" | "muted" {
  return {
    pending: "muted",
    in_transit: "info",
    out_for_delivery: "warning",
    delivered: "success",
    exception: "destructive",
  }[s] as "info" | "warning" | "success" | "destructive" | "muted";
}
